import { Component, Input } from '@angular/core';
import { IconComponent, IconName } from '../icon/icon.component';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'warning' | 'success' | 'ghost' | 'ghost-danger' | 'ghost-primary';
export type ButtonSize = 'sm' | 'md';

const BASE = 'inline-flex items-center justify-center gap-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-gradient-to-r from-brand-600 to-accent-500 text-white font-semibold hover:opacity-90 transition-opacity',
  secondary: 'bg-white text-gray-700 font-semibold border border-gray-300 hover:bg-gray-50',
  danger: 'bg-white text-red-600 font-semibold border border-red-300 hover:bg-red-50',
  // Same soft-outline treatment as danger, just a different semantic hue —
  // "needs a human"/"done" states, not brand actions, so they stay off the
  // brand gradient the way danger does.
  warning: 'bg-white text-orange-600 font-semibold border border-orange-300 hover:bg-orange-50',
  success: 'bg-white text-green-600 font-semibold border border-green-300 hover:bg-green-50',
  ghost: 'text-gray-500 font-medium hover:text-gray-700 hover:bg-gray-50',
  'ghost-danger': 'text-red-600 font-medium hover:text-red-700 hover:bg-red-50',
  'ghost-primary': 'text-brand-600 font-medium hover:text-brand-700 hover:bg-brand-50',
};

const SIZE_CLASSES: Record<ButtonVariant, Record<ButtonSize, string>> = {
  primary: { md: 'px-5 py-2.5 text-sm', sm: 'px-3 py-1.5 text-xs' },
  secondary: { md: 'px-5 py-2.5 text-sm', sm: 'px-3 py-1.5 text-xs' },
  danger: { md: 'px-5 py-2.5 text-sm', sm: 'px-3 py-1.5 text-xs' },
  warning: { md: 'px-5 py-2.5 text-sm', sm: 'px-3 py-1.5 text-xs' },
  success: { md: 'px-5 py-2.5 text-sm', sm: 'px-3 py-1.5 text-xs' },
  ghost: { md: 'px-3 py-2 text-sm', sm: 'px-2 py-1 text-xs' },
  'ghost-danger': { md: 'px-3 py-2 text-sm', sm: 'px-2 py-1 text-xs' },
  'ghost-primary': { md: 'px-3 py-2 text-sm', sm: 'px-2 py-1 text-xs' },
};

/**
 * Every button "role" in the dashboard (save/primary, cancel, delete,
 * inline text-link action) used to be styled ad hoc per page — three
 * different paddings for "Save", two unrelated treatments for "Cancel",
 * no shared pattern for destructive actions. This is the one place those
 * roles are defined; pages pick a variant, not a class string.
 */
@Component({
  selector: 'app-button',
  standalone: true,
  imports: [IconComponent],
  host: {
    // Custom elements default to inline display, which silently ignores a
    // w-full request on the inner <button> — full-width auth-form submit
    // buttons need the host itself to give up its width to the button.
    '[class.block]': 'fullWidth',
    '[class.w-full]': 'fullWidth',
  },
  template: `
    <button
      [type]="type"
      [disabled]="disabled"
      [class]="classes"
      [attr.aria-label]="ariaLabel || null"
    >
      @if (icon) {
        <app-icon [name]="icon" [size]="iconSize" />
      }
      <ng-content></ng-content>
    </button>
  `,
})
export class ButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() type: 'button' | 'submit' = 'button';
  @Input() disabled = false;
  @Input() icon?: IconName;
  @Input() ariaLabel?: string;
  @Input() fullWidth = false;

  get iconSize(): number {
    return this.size === 'sm' ? 14 : 16;
  }

  get classes(): string {
    const width = this.fullWidth ? ' w-full' : '';
    return `${BASE}${width} ${VARIANT_CLASSES[this.variant]} ${SIZE_CLASSES[this.variant][this.size]}`;
  }
}
